import { useState, useCallback, useEffect } from 'react';
import borisApiService from './BorisApiService';

export const useBorisChatApi = (autoCreateSession = true, userId = null) => {
  const [sessionId, setSessionId] = useState(null);
  const [userName, setUserName] = useState(null); // Add this line
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  // Initialize session on mount if autoCreateSession is true
  useEffect(() => {
    if (autoCreateSession && !sessionId) {
      createSession(userId);
    }
  }, [autoCreateSession, userId, sessionId]);

  // Test connection on mount
  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = useCallback(async () => {
    try {
      const connected = await borisApiService.testConnection();
      setIsConnected(connected);
      return connected;
    } catch (error) {
      setIsConnected(false);
      setError(`Connection failed: ${error.message}`);
      return false;
    }
  }, []);

  const createSession = useCallback(async (userId = null, intent = 'discover_category', frontend_user_id = userId) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const session = await borisApiService.createSession(userId, intent, frontend_user_id);
      setSessionId(session.session_id);
      
      // Capture user_name from session response
      if (session.user_name) {
        setUserName(session.user_name);
      }
      
      return session;
    } catch (error) {
      setError(`Failed to create session: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const sendMessage = useCallback(async (message, uiEvent = null, context = null) => {
    if (!sessionId) {
      const error = new Error('No active session');
      setError(error.message);
      throw error;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await borisApiService.sendMessage(message, uiEvent, context);
      
      // Check for escalation signals in the response
      if (response.signals && response.signals.escalation_required) {
        console.log('🚨 Escalation signal detected:', response.signals);
        // The escalation will be handled by the consuming component
      }
      
      return response;
    } catch (error) {
      setError(`Failed to send message: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  const handleUIEvent = useCallback(async (event) => {
    return await sendMessage(null, event);
  }, [sendMessage]);

  const sendChipSelection = useCallback(async (chipId, chipData = null) => {
    return await borisApiService.sendChipSelection(chipId, chipData);
  }, []);

  const sendCTAClick = useCallback(async (action, args = {}) => {
    return await borisApiService.sendCTAClick(action, args);
  }, []);

  const sendOptionSelection = useCallback(async (optionId, groupTitle, optionData = null) => {
    return await borisApiService.sendOptionSelection(optionId, groupTitle, optionData);
  }, []);

  const sendQuickReply = useCallback(async (questionText, intent = null) => {
    return await borisApiService.sendQuickReply(questionText, intent);
  }, []);

  const getSessionAnalytics = useCallback(async () => {
    if (!sessionId) {
      throw new Error('No active session');
    }
    
    try {
      return await borisApiService.getSessionAnalytics(sessionId);
    } catch (error) {
      setError(`Failed to get analytics: ${error.message}`);
      throw error;
    }
  }, [sessionId]);

  const getComprehensiveAnalytics = useCallback(async () => {
    if (!sessionId) {
      throw new Error('No active session');
    }
    
    try {
      return await borisApiService.getComprehensiveAnalytics(sessionId);
    } catch (error) {
      setError(`Failed to get comprehensive analytics: ${error.message}`);
      throw error;
    }
  }, [sessionId]);

  const endSession = useCallback(async () => {
    await borisApiService.endSession();
    setSessionId(null);
  }, []);

  const submitEscalation = useCallback(async (escalationData) => {
    try {
      return await borisApiService.submitEscalation(escalationData);
    } catch (error) {
      setError(`Failed to submit escalation: ${error.message}`);
      throw error;
    }
  }, []);

  const getEscalation = useCallback(async (ticketId) => {
    try {
      return await borisApiService.getEscalation(ticketId);
    } catch (error) {
      setError(`Failed to get escalation: ${error.message}`);
      throw error;
    }
  }, []);

  const createUser = useCallback(async (userData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const user = await borisApiService.createUser(userData);
      return user;
    } catch (error) {
      setError(`Failed to create user: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateUser = useCallback(async (userId, userData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const user = await borisApiService.updateUser(userId, userData);
      return user;
    } catch (error) {
      setError(`Failed to update user: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    sessionId,
    userName, // Add this line to return userName
    isLoading,
    error,
    isConnected,
    
    // User Management
    createUser,
    updateUser,
    
    // Session Management
    createSession,
    endSession,
    
    // Chat Methods
    sendMessage,
    handleUIEvent,
    sendChipSelection,
    sendCTAClick,
    sendOptionSelection,
    sendQuickReply,
    
    // Analytics
    getSessionAnalytics,
    getComprehensiveAnalytics,
    
    // Escalation
    submitEscalation,
    getEscalation,
    
    // Utils
    checkConnection,
    clearError,
    
    // Direct service access for advanced usage
    apiService: borisApiService
  };
};

export default useBorisChatApi;
